import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  Building2,
  Users,
  TrendingUp,
  Briefcase,
  Star,
  ArrowRight,
  CreditCard,
} from 'lucide-react';
import Layout from '../../components/Layout';

const CANDIDATOS_SIMULADOS = [
  {
    id: 1,
    nome: 'Carlos Almeida',
    cidade: 'Rio Branco — AC',
    area: 'Tecnologia da Informação',
    match: 92,
    disponivel: true,
  },
  {
    id: 2,
    nome: 'Fernanda Costa',
    cidade: 'Cruzeiro do Sul — AC',
    area: 'Administração',
    match: 88,
    disponivel: true,
  },
  {
    id: 3,
    nome: 'João Mendes',
    cidade: 'Rio Branco — AC',
    area: 'Engenharia Civil',
    match: 75,
    disponivel: false,
  },
];

const KPIS = [
  {
    label: 'Candidatos Recebidos',
    valor: 12,
    icon: Users,
    cor: 'text-acre-green',
    bg: 'bg-acre-light',
  },
  {
    label: 'Em Seleção',
    valor: 3,
    icon: TrendingUp,
    cor: 'text-acre-gold',
    bg: 'bg-acre-gold/10',
  },
  {
    label: 'Vagas Ativas',
    valor: 2,
    icon: Briefcase,
    cor: 'text-acre-teal',
    bg: 'bg-acre-teal/10',
  },
  {
    label: 'Match Médio',
    valor: '85%',
    icon: Star,
    cor: 'text-acre-green',
    bg: 'bg-green-50',
  },
];

const JORNADA_EMPRESA = [
  { numero: 1, titulo: 'Cadastro', descricao: 'Cria conta e cadastra a empresa', icon: '🏢' },
  { numero: 2, titulo: 'Publicar Vaga', descricao: 'Descreve a vaga e requisitos', icon: '📋' },
  { numero: 3, titulo: 'Selecionar', descricao: 'Analisa candidatos com IA Match', icon: '🎯' },
  { numero: 4, titulo: 'Contratar', descricao: 'Contata candidato e fecha contrato', icon: '🤝' },
];

const PLANOS_EMPRESA = [
  { dias: 30, preco: 97, pix: 87.3, label: '30 dias' },
  { dias: 60, preco: 167, pix: 150.3, label: '60 dias', popular: true },
  { dias: 90, preco: 227, pix: 204.3, label: '90 dias' },
];

function MatchBadge({ valor }) {
  const cor =
    valor >= 85
      ? 'bg-green-100 text-green-700 border-green-300'
      : valor >= 75
      ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
      : 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-bold ${cor}`}
    >
      <Star size={11} />
      {valor}% match
    </span>
  );
}

export default function PreviewEmpresa() {
  const navigate = useNavigate();

  return (
    <Layout title="Preview — Empresa" subtitle="Veja a plataforma como uma empresa vê">
      {/* Header explicativo */}
      <div className="bg-acre-dark rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-acre-gold/20 flex items-center justify-center">
            <Eye size={20} className="text-acre-gold" />
          </div>
          <div>
            <h2 className="font-sora font-bold text-white text-base">
              Visualização — Perspectiva da Empresa
            </h2>
            <p className="text-gray-400 text-xs mt-0.5">
              Esta página simula o que uma empresa vê ao usar a plataforma
            </p>
          </div>
        </div>
        <span className="self-start sm:self-auto inline-flex items-center gap-1.5 bg-acre-gold/20 border border-acre-gold/40 text-acre-gold text-xs font-bold px-3 py-1.5 rounded-full">
          <Eye size={12} />
          Modo Admin — Visualização
        </span>
      </div>

      {/* KPIs */}
      <h3 className="section-title mb-4">Dashboard da Empresa (simulado)</h3>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {KPIS.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="card p-4">
              <div className={`w-9 h-9 rounded-xl ${kpi.bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={kpi.cor} />
              </div>
              <p className={`font-sora font-black text-2xl ${kpi.cor}`}>{kpi.valor}</p>
              <p className="text-xs text-gray-400 mt-0.5">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Lista de candidatos */}
      <h3 className="section-title mb-4">Candidatos com Match (simulado)</h3>

      <div className="space-y-3 mb-8">
        {CANDIDATOS_SIMULADOS.map((c) => (
          <div
            key={c.id}
            className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-acre-light flex items-center justify-center flex-shrink-0">
                <span className="font-sora font-black text-acre-green text-sm">
                  {c.nome.charAt(0)}
                </span>
              </div>
              <div>
                <p className="font-semibold text-acre-dark text-sm">{c.nome}</p>
                <p className="text-xs text-gray-500">{c.area}</p>
                <p className="text-xs text-gray-400">{c.cidade}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {c.disponivel ? (
                <span className="text-xs bg-green-50 text-green-600 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                  Disponível
                </span>
              ) : (
                <span className="text-xs bg-gray-100 text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full font-medium">
                  Indisponível
                </span>
              )}
              <MatchBadge valor={c.match} />
              <button className="text-xs bg-acre-dark text-white px-3 py-1.5 rounded-lg font-bold hover:bg-acre-green transition-colors">
                Ver perfil
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Jornada da Empresa */}
      <h3 className="section-title mb-4">Jornada da Empresa</h3>

      <div className="card p-6 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {JORNADA_EMPRESA.map((step) => (
            <div key={step.numero} className="flex flex-col items-center text-center">
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-acre-gold/10 flex items-center justify-center text-2xl mb-3">
                  {step.icon}
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-acre-gold flex items-center justify-center">
                  <span className="text-acre-dark text-xs font-black">{step.numero}</span>
                </div>
              </div>
              <p className="font-sora font-bold text-acre-dark text-sm mb-1">{step.titulo}</p>
              <p className="text-xs text-gray-400 leading-snug">{step.descricao}</p>
            </div>
          ))}
        </div>
        <div className="hidden sm:flex items-center justify-center gap-2 mt-4">
          {JORNADA_EMPRESA.map((step, idx) => (
            <React.Fragment key={step.numero}>
              <span className="text-xs font-bold text-acre-gold bg-acre-gold/10 px-2 py-0.5 rounded-full">
                {step.titulo}
              </span>
              {idx < JORNADA_EMPRESA.length - 1 && (
                <ArrowRight size={14} className="text-gray-300" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Planos e preços */}
      <h3 className="section-title mb-4">Planos e Preços</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {PLANOS_EMPRESA.map((plano) => (
          <div
            key={plano.dias}
            className={`card p-5 relative ${plano.popular ? 'border-2 border-acre-green shadow-md' : ''}`}
          >
            {plano.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-acre-green text-white text-xs font-black px-3 py-0.5 rounded-full whitespace-nowrap">
                ⭐ Mais Popular
              </span>
            )}
            <p className="font-sora font-bold text-acre-dark text-base mb-1">{plano.label}</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-sora font-black text-2xl text-acre-dark">
                R$ {plano.preco.toFixed(2).replace('.', ',')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mb-1">
              <CreditCard size={12} className="text-gray-400" />
              <span className="text-xs text-gray-500">Cartão: R$ {plano.preco.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-green-600">PIX (10% off):</span>
              <span className="text-xs font-black text-green-700">
                R$ {plano.pix.toFixed(2).replace('.', ',')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Botão */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate('/admin/empresas')}
          className="inline-flex items-center gap-2 bg-acre-dark text-white font-bold px-5 py-3 rounded-xl hover:bg-acre-gold hover:text-acre-dark transition-colors"
        >
          <Building2 size={16} />
          Ver empresas reais
        </button>
      </div>
    </Layout>
  );
}
